import { createSignal, onMount, Show } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { track, trackBeacon } from "~/lib/analytics";
import { handleAuthCallback, takeReturnTo } from "~/lib/auth";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(true);

  onMount(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");

    if (oauthError) {
      const message = params.get("error_description") ?? oauthError;
      trackBeacon("login_failed", { reason: oauthError }, { page: "/auth/callback" });
      setError(message);
      setBusy(false);
      return;
    }

    if (!code || !state) {
      setError("Missing authorization code");
      setBusy(false);
      return;
    }

    try {
      await handleAuthCallback(code, state);
      track("login_completed", undefined, { page: "/auth/callback" });
      const returnTo = takeReturnTo();
      if (returnTo?.startsWith("/meetup/")) {
        const slug = decodeURIComponent(returnTo.slice("/meetup/".length).split("?")[0] ?? "");
        navigate({ to: "/meetup/$slug", params: { slug } });
        return;
      }
      if (returnTo === "/meetup") {
        navigate({ to: "/meetup" });
        return;
      }
      navigate({ to: "/" });
    } catch (err) {
      trackBeacon("login_failed", { reason: "callback_error" }, { page: "/auth/callback" });
      setError((err as Error).message);
      setBusy(false);
    }
  });

  return (
    <section class="card">
      <h1>Signing you in</h1>
      <Show when={busy() && !error()}>
        <p class="muted">Completing Zitadel login…</p>
      </Show>
      <Show when={error()}>
        <div class="error-box">{error()}</div>
        <p class="muted">
          <a href="/login">Back to sign in</a>
        </p>
      </Show>
    </section>
  );
}